:host {
    color-scheme: light;
    position: fixed;
    bottom: 0;
    right: 0;
    width: 0;
    height: 0;
    z-index: 2147483647;
    pointer-events: none;
    font-family:
        ui-sans-serif,
        system-ui,
        -apple-system,
        'Segoe UI',
        sans-serif;
    font-size: 16px;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
    box-sizing: border-box;
}

:host {
    --op-primary: oklch(0.546 0.245 262.881);
    --op-primary-ink: oklch(0.985 0 0);
    --op-muted: oklch(0.963 0.002 197.1);
    --op-text: oklch(0.148 0.004 228.8);
    --op-subtle: oklch(0.56 0.021 213.5);
    --op-line: oklch(0.925 0.005 214.3);
    --op-card: oklch(1 0 0);
    --op-danger: oklch(0.577 0.245 27.325);
    --op-danger-foreground: oklch(0.985 0 0);
    --op-green: oklch(0.68 0.176 146.661);
    --op-brand: oklch(0.546 0.245 262.881);
    --op-brand-deep: oklch(0.282 0.091 267.935);
    --op-brand-spark: oklch(0.704 0.14 182.503);
}

.panel {
    position: absolute;
    bottom: 80px;
    right: 16px;
    width: min(400px, calc(100vw - 32px));
    height: 520px;
    max-height: calc(100dvh - 100px);
    pointer-events: auto;
    opacity: 0;
    transform: translateY(8px);
    transform-origin: bottom right;
    transition:
        opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

.panel[data-open='true'] {
    opacity: 1;
    transform: none;
}

.panel[data-open='false'] {
    pointer-events: none;
}

.panel > div {
    width: 100%;
    height: 100%;
}

:host([data-pos='left']) .panel {
    right: auto;
    left: 16px;
    transform-origin: bottom left;
}

:host([data-pos='left']) .bubble {
    right: auto;
    left: 16px;
}

:host([data-pos='left']) .bubble::after {
    right: auto;
    left: calc(100% + 10px);
    transform: translateY(-50%) translateX(-4px);
}

:host([data-pos='left']) .bubble:hover::after,
:host([data-pos='left']) .bubble:focus-visible::after {
    transform: translateY(-50%) translateX(0);
}

.bubble {
    position: absolute;
    bottom: 16px;
    right: 16px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--op-primary);
    color: var(--op-primary-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    box-shadow:
        0 4px 12px rgb(0 0 0 / 16%),
        0 1px 3px rgb(0 0 0 / 10%);
    opacity: 0;
    transition:
        opacity 200ms ease,
        transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 200ms cubic-bezier(0.16, 1, 0.3, 1);
    -webkit-tap-highlight-color: transparent;
    outline: none;
    padding: 0;
}

:host([data-op-ready]) .bubble {
    opacity: 1;
}

.bubble:hover {
    transform: scale(1.06);
}

.bubble:active {
    transform: scale(0.95);
}

.bubble:focus-visible {
    box-shadow:
        0 0 0 3px var(--op-card),
        0 0 0 5px var(--op-primary);
}

.bubble svg {
    width: 24px;
    height: 24px;
}

.bubble::after {
    content: attr(data-tip);
    position: absolute;
    top: 50%;
    right: calc(100% + 10px);
    white-space: nowrap;
    background: var(--op-text);
    color: var(--op-card);
    font-size: 12px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 16%);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50%) translateX(4px);
    transition:
        opacity 150ms ease,
        transform 150ms ease;
}

.bubble:hover::after,
.bubble:focus-visible::after {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
}

@media (pointer: coarse) {
    .bubble::after {
        display: none;
    }
}

.op-card {
    position: relative;
    width: 100%;
    height: 100%;
    background: var(--op-card);
    color: var(--op-text);
    border: none;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.op-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--op-card);
    color: var(--op-text);
    border-bottom: 1px solid var(--op-line);
}

.op-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--op-primary);
    color: var(--op-primary-ink);
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.op-titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.op-header .op-avatar {
    background: var(--op-primary);
    color: var(--op-primary-ink);
}

.op-header .op-status {
    color: inherit;
    opacity: 0.85;
}

.op-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
}

.op-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--op-green);
}

.op-status-connecting {
    color: var(--op-subtle);
}

.op-status-connecting .op-dot {
    background: var(--op-subtle);
    animation: op-pulse 1.2s ease-in-out infinite;
}

.op-status-offline {
    color: var(--op-danger);
}

.op-status-offline .op-dot {
    background: var(--op-danger);
}

@keyframes op-pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.35;
    }
}

.op-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--op-green);
}

.op-close {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: inherit;
    opacity: 0.8;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.op-close:hover {
    background: rgb(0 0 0 / 6%);
    color: inherit;
    opacity: 1;
}

.op-close:focus-visible {
    outline: 2px solid var(--op-primary);
    outline-offset: 2px;
}

.op-close svg {
    width: 18px;
    height: 18px;
}

.op-messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px;
    scrollbar-width: thin;
    scrollbar-color: var(--op-line) transparent;
}

.op-messages::-webkit-scrollbar {
    width: 8px;
}

.op-messages::-webkit-scrollbar-track {
    background: transparent;
}

.op-messages::-webkit-scrollbar-thumb {
    background-color: var(--op-line);
    border-radius: 9999px;
    border: 2px solid transparent;
    background-clip: padding-box;
}

.op-messages::-webkit-scrollbar-thumb:hover {
    background-color: var(--op-subtle);
}

.op-row {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    margin-top: 2px;
}

.op-group-start {
    margin-top: 10px;
}

.op-row-out {
    flex-direction: row-reverse;
}

.op-tick {
    align-self: flex-end;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--op-subtle);
    margin-bottom: 2px;
}

span.op-tick svg {
    width: 14px;
    height: 14px;
    animation: op-spin 1s linear infinite;
}

button.op-tick svg {
    width: 14px;
    height: 14px;
}

@keyframes op-spin {
    to {
        transform: rotate(360deg);
    }
}

.op-tick[aria-hidden='true'] {
    visibility: hidden;
}

button.op-tick {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}

.op-tick-retry {
    align-self: flex-end;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--op-danger);
    margin-bottom: 2px;
}

.op-tick-retry svg {
    width: 14px;
    height: 14px;
}

.op-retry-btn {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    color: var(--op-danger);
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}

.op-bubble-wrap {
    display: flex;
    flex-direction: column;
    max-width: 78%;
}

.op-bubble {
    padding: 8px 12px;
    font-size: 15px;
    line-height: 1.45;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    box-shadow: 0 1px 2px rgb(0 0 0 / 6%);
}

.op-bubble-in {
    background: var(--op-muted);
    color: var(--op-text);
}

.op-bubble-out {
    background: color-mix(in oklch, var(--op-primary) 92%, var(--op-card));
    color: var(--op-primary-ink);
}

.op-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.op-link:hover {
    text-decoration: none;
}

.op-bubble-in .op-link {
    color: var(--op-primary);
}

.op-pos-solo.op-bubble-in {
    border-radius: 16px 16px 16px 4px;
}

.op-pos-first.op-bubble-in {
    border-radius: 16px 16px 16px 4px;
}

.op-pos-middle.op-bubble-in {
    border-radius: 4px 16px 16px 4px;
}

.op-pos-last.op-bubble-in {
    border-radius: 4px 16px 16px 16px;
}

.op-pos-solo.op-bubble-out {
    border-radius: 16px 16px 4px 16px;
}

.op-pos-first.op-bubble-out {
    border-radius: 16px 16px 4px 16px;
}

.op-pos-middle.op-bubble-out {
    border-radius: 16px 4px 4px 16px;
}

.op-pos-last.op-bubble-out {
    border-radius: 16px 4px 16px 16px;
}

.op-typing {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
}

.op-typing-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--op-subtle);
    animation: op-typing 1.2s ease-in-out infinite;
}

.op-typing-dot:nth-child(2) {
    animation-delay: 0.15s;
}

.op-typing-dot:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes op-typing {
    0%,
    60%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }

    30% {
        transform: translateY(-4px);
        opacity: 1;
    }
}

.op-meta {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
    font-size: 11px;
    line-height: 1;
    color: var(--op-subtle);
}

.op-bubble-out .op-meta {
    color: color-mix(in oklch, var(--op-primary-ink) 70%, transparent);
}

.op-error {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 24px;
    font-size: 14px;
    color: var(--op-subtle);
    text-align: center;
}

.op-error p {
    margin: 0;
}

.op-error-hint {
    font-size: 12px;
}

.op-welcome {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px 16px;
    text-align: center;
}

.op-welcome-sub {
    margin: 0;
    font-size: 13px;
    color: var(--op-subtle);
}

.op-day {
    display: flex;
    justify-content: center;
    margin: 12px 0 2px;
}

.op-day span {
    font-size: 11px;
    font-weight: 600;
    color: var(--op-subtle);
    background: var(--op-muted);
    border-radius: 999px;
    padding: 3px 10px;
}

.op-loading {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
}

.op-shimmer {
    height: 38px;
    border-radius: 16px;
    background: linear-gradient(
        100deg,
        var(--op-muted) 40%,
        color-mix(in oklch, var(--op-muted) 60%, white) 50%,
        var(--op-muted) 60%
    );
    background-size: 200% 100%;
    animation: op-shimmer 1.4s ease infinite;
}

.op-shimmer-in {
    width: 65%;
}

.op-shimmer-out {
    width: 55%;
    align-self: flex-end;
}

.op-shimmer-short {
    width: 40%;
}

@keyframes op-shimmer {
    0% {
        background-position: 180% 0;
    }

    100% {
        background-position: -80% 0;
    }
}

.bubble-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    border-radius: 999px;
    border: 2px solid var(--op-card);
    background: color-mix(in oklch, var(--op-danger) 88%, var(--op-card));
    color: var(--op-danger-foreground);
    font-size: 11px;
    font-weight: 700;
    padding: 0 5px;
}

.bubble-badge[hidden] {
    display: none;
}

.op-jump {
    position: absolute;
    right: 12px;
    bottom: 88px;
    z-index: 2;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--op-line);
    border-radius: 50%;
    background: var(--op-card);
    color: var(--op-subtle);
    cursor: pointer;
    padding: 0;
    box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
}

.op-jump-count {
    position: absolute;
    top: -4px;
    right: -4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: var(--op-primary);
    color: var(--op-primary-ink);
    font-size: 11px;
    font-weight: 700;
    padding: 0 4px;
}

.op-composer {
    border-top: 1px solid var(--op-line);
    padding: 10px 10px 6px;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: var(--op-card);
}

.op-credit {
    --brand: var(--op-brand);
    --brand-spark: var(--op-brand-spark);

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    flex-shrink: 0;
    width: fit-content;
    margin-inline: auto;
    padding: 8px 0 calc(8px + env(safe-area-inset-bottom));
    background: var(--op-card);
    color: color-mix(in oklch, var(--op-subtle) 85%, transparent);
    font-size: 11px;
    line-height: 1.2;
    text-decoration: none;
}

.op-credit:hover .op-credit-name {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.op-credit-logo {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    margin-left: 8px;
    margin-right: 2px;
}

.op-credit-name {
    font-weight: 600;
    letter-spacing: -0.01em;
}

.op-credit-order {
    color: var(--op-brand-deep);
}

.op-credit-pilot {
    color: var(--op-brand);
}

.op-input {
    flex: 1;
    min-width: 0;
    resize: none;
    overflow-y: auto;
    scrollbar-width: none;
    border: 1px solid var(--op-line);
    border-radius: 20px;
    background: var(--op-card);
    color: var(--op-text);
    caret-color: var(--op-primary);
    font: inherit;
    font-size: 15px;
    line-height: 1.4;
    padding: 9px 14px;
    max-height: 120px;
    outline: none;
}

.op-input::-webkit-scrollbar {
    display: none;
}

.op-input:focus {
    border-color: var(--op-primary);
}

.op-input::placeholder {
    color: var(--op-subtle);
}

.op-card ::selection {
    background: color-mix(in oklch, var(--op-primary) 25%, transparent);
}

.op-send:focus-visible,
.op-jump:focus-visible,
.op-credit:focus-visible,
.op-retry-btn:focus-visible {
    outline: 2px solid var(--op-primary);
    outline-offset: 2px;
}

.op-send {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: var(--op-primary);
    color: var(--op-primary-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
}

.op-send:disabled {
    opacity: 0.45;
    cursor: default;
}

.op-send svg {
    width: 18px;
    height: 18px;
    transform: translateX(-1px);
}

.op-jump svg {
    width: 16px;
    height: 16px;
}

@media (max-width: 480px) {
    .panel {
        position: fixed;
        left: 12px;
        right: 12px;
        bottom: 88px;
        width: auto;
        height: min(600px, calc(100dvh - 110px));
        max-height: calc(100dvh - 110px);
        transform-origin: bottom right;
    }

    .op-card {
        border-radius: 16px;
    }

    :host([data-pos='left']) {
        right: auto;
        left: 0;
    }

    :host([data-pos='left']) .panel {
        transform-origin: bottom left;
    }
}

@media (prefers-reduced-motion: reduce) {
    .panel,
    .bubble {
        transition: none;
    }

    .op-shimmer,
    .op-status-connecting .op-dot,
    .op-typing-dot,
    span.op-tick svg {
        animation: none;
    }
}
